import { FC, MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import Ellipse from '@monofront/design-system/visuals/Icons/Ellipse';
import LeftNavigationArrow from '../../visuals/Icons/LeftNavigationArrow';
import RightNavigationArrow from '../../visuals/Icons/RightNavigationArrow';
import Unit from './Unit';

interface IPaginationProps {
  message: string;
  pages: number[];
  currentPage: number;
  previousPage: number;
  nextPage: number;
  firstPage: number;
  lastPage: number;
  displayPagination: boolean;
  displayGoToPreviousPage: boolean;
  displayGoToNextPage: boolean;
  displayGoToFirstPage: boolean;
  displayGoToFirstPageEllipsis: boolean;
  displayGoToLastPage: boolean;
  displayGoToLastPageEllipsis: boolean;
  createURL: (page: number) => string;
  loadPageResults: (event: MouseEvent<HTMLAnchorElement>, page: number) => void;
}

const Pagination: FC<IPaginationProps> = ({
  message,
  pages,
  currentPage,
  previousPage,
  nextPage,
  firstPage,
  lastPage,
  displayPagination,
  displayGoToPreviousPage,
  displayGoToNextPage,
  displayGoToFirstPage,
  displayGoToFirstPageEllipsis,
  displayGoToLastPage,
  displayGoToLastPageEllipsis,
  createURL,
  loadPageResults,
}: IPaginationProps) => {
  const tActions = useTranslations('Actions');

  return (
    <div className="tw-mb-6 tw-mt-8 tw-flex tw-flex-col tw-items-center tw-gap-y-2">
      <p
        data-testid={`Pagination summary`}
        className="tw-px-4 tw-py-2 tw-text-center tw-text-sm"
      >
        {message}
      </p>
      {displayPagination && (
        <nav
          data-testid="Pagination"
          className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1"
          aria-label="Pagination"
          role="navigation"
        >
          {displayGoToPreviousPage && (
            <a
              className="tw-lf-button tw-lf-button-secondary tw-size-11 tw-rounded-full tw-p-0"
              aria-label={tActions('previous')}
              href={createURL(previousPage)}
              onClick={(event) => loadPageResults(event, previousPage)}
            >
              <LeftNavigationArrow />
            </a>
          )}
          {displayGoToFirstPage && (
            <>
              <Unit
                isActive={false}
                href={createURL(firstPage)}
                onClick={(event) => loadPageResults(event, firstPage)}
              >
                {firstPage + 1}
              </Unit>
              {displayGoToFirstPageEllipsis && (
                <Ellipse
                  UNSAFE_className="tw-py-1 tw-h-8 tw-place-self-end"
                  data-testid="Pagination ellipsis"
                  aria-hidden="true"
                />
              )}
            </>
          )}
          {pages.map((page) => {
            const label = page + 1;
            const isActive = page === currentPage;

            return (
              <Unit
                key={page}
                isActive={isActive}
                href={createURL(page)}
                onClick={(event) => loadPageResults(event, page)}
              >
                {label}
              </Unit>
            );
          })}
          {displayGoToLastPage && (
            <>
              {displayGoToLastPageEllipsis && (
                <Ellipse
                  UNSAFE_className="tw-py-1 tw-h-8 tw-place-self-end"
                  data-testid="Pagination ellipsis"
                  aria-hidden="true"
                />
              )}
              <Unit
                isActive={false}
                href={createURL(lastPage)}
                onClick={(event) => loadPageResults(event, lastPage)}
              >
                {lastPage + 1}
              </Unit>
            </>
          )}
          {displayGoToNextPage && (
            <a
              className="tw-lf-button tw-lf-button-secondary tw-size-11 tw-rounded-full tw-p-0"
              aria-label={tActions('next')}
              href={createURL(nextPage)}
              onClick={(event) => loadPageResults(event, nextPage)}
            >
              <RightNavigationArrow />
            </a>
          )}
        </nav>
      )}
    </div>
  );
};

export default Pagination;
